import { type MutableRefObject, useEffect, useRef } from 'react'

import { Divider, Form, Space, WithFormControlSelect } from '@procurify/ui'
import { FormikProvider, useFormik } from 'formik'
import type moment from 'moment'
import * as Yup from 'yup'

import {
  RequestableCreditCardSelect,
  type RequestableCreditCardOption,
} from '@webapp/components/RequestableCreditCardSelect'
import {
  type Currency,
  type CurrencyName,
} from '@webapp/core/entities/external/Currency'
import { useFeatureFlag, useProcurifyIntl } from '@webapp/hooks'
import {
  computeExpenseLineItemTotalsUseCase,
  parsePercentageOrZero,
  round2,
} from '@webapp/modules/expense/business-logic/use-cases/ComputeExpenseLineItemTotals'
import {
  ExpenseAccountCodeSelect,
  type ExpenseAccountSelectValue,
} from '@webapp/modules/expense/components/ExpenseAccountCodeSelect'
import {
  ExpenseFormReimbursementCustomizations,
  ExpenseMileageConfigurationTaxTypes,
  MileageRatePresetUnits,
  UnitsOfDistance,
  type ExpenseFormReimbursementCustomization,
} from '@webapp/modules/expense/entities/enums'
import {
  ExpenseTypes,
  type ExpenseTypeEnum,
} from '@webapp/modules/expense/entities/enums/ExpenseTypeEnum'
import { type ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import { type RequestMileageConfiguration } from '@webapp/modules/expense/entities/RequestMileageConfiguration'
import { AirfareFields } from './components/AirfareFields'
import { DefaultFields } from './components/DefaultFields'
import { ExpenseTypeSelector } from './components/ExpenseTypeSelector/ExpenseTypeSelector'
import { LabourFields } from './components/LabourFields'
import { MealsFields } from './components/MealsFields'
import { MileageFields } from './components/MileageFields'
import { CUSTOM_TAX_TYPE_OPTIONS } from './components/TaxFields'
import { mapExpenseItemToFormSchema } from './mapExpenseItemToFormSchema'
import messages from './messages'
import {
  type ExpenseMileageConfiguration,
  type MileageRatePreset,
} from '../../entities'
import {
  type ExpenseTaxSelectValue,
  isCustomTax,
  toTaxMode,
} from '../ExpenseTaxSelect'

export type SelectFieldValue = { value: string; label: string }

export interface ExpenseLineItemFormSchema {
  reimbursable: boolean
  merchant: string
  transDate: moment.Moment | null
  subtotal: number
  taxAmount: number
  taxType: ExpenseTaxSelectValue
  notes: string
  expenseType: ExpenseTypeEnum
  tips: number
  mileageRate: number
  mileageRatePreset: Nullable<MileageRatePreset>
  distance: number
  unitOfDistanceStr: SelectFieldValue
  hourlyRate: number
  hoursOfWork: number
  airline: string
  ticketNumber: string
  departureDate: moment.Moment | null
  arrivalDate: moment.Moment | null
  account: ExpenseAccountSelectValue | null
  attachment: string
  paymentMethod: RequestableCreditCardOption | null
  currency: Currency | null
  totalCost: number
}

export interface ExpenseLineItemFormHandle {
  submitForm: () => Promise<void>
}

export interface IExpenseLineItemFormProps {
  item?: ExpenseItem
  baseCurrency?: Currency | null
  reimbursementSetting: ExpenseFormReimbursementCustomization
  secondaryCategoryId?: number
  requesterId: number
  attachment: string
  isSubmitting: boolean
  onSubmit: (values: ExpenseLineItemFormSchema) => void
  formRef?: MutableRefObject<ExpenseLineItemFormHandle | null>
  mileageConfiguration?: Nullable<ExpenseMileageConfiguration>
  /**
   * To be removed with PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS
   */
  mileageLockSetting?: RequestMileageConfiguration | null
}

export const ExpenseLineItemForm = ({
  item,
  baseCurrency,
  reimbursementSetting,
  secondaryCategoryId,
  requesterId,
  attachment,
  isSubmitting,
  onSubmit,
  formRef,
  mileageConfiguration = null,
  mileageLockSetting = null,
}: IExpenseLineItemFormProps) => {
  const intl = useProcurifyIntl()
  const PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS = useFeatureFlag(
    'PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS'
  )

  const isReimbursementLocked =
    reimbursementSetting ===
      ExpenseFormReimbursementCustomizations.ALWAYS_YES ||
    reimbursementSetting === ExpenseFormReimbursementCustomizations.ALWAYS_NO

  const validationSchema = Yup.object<ExpenseLineItemFormSchema>({
    reimbursable: Yup.boolean().required(),
    merchant: Yup.string()
      .required(intl.formatMessage(messages.vendorNameRequired))
      .trim(),
    transDate: Yup.mixed().required(intl.formatMessage(messages.dateRequired)),
    subtotal: Yup.number().required(),
    taxAmount: Yup.number().required(),
    taxType: Yup.object<ExpenseTaxSelectValue>().required(),
    notes: Yup.string(),
    expenseType: Yup.mixed<ExpenseTypeEnum>().required(),
    tips: Yup.number(),
    mileageRate: Yup.number(),
    mileageRatePreset: Yup.object<MileageRatePreset>().nullable(),
    distance: Yup.number(),
    unitOfDistanceStr: Yup.object<SelectFieldValue>().required(),
    hourlyRate: Yup.number(),
    hoursOfWork: Yup.number(),
    airline: Yup.string(),
    ticketNumber: Yup.string(),
    departureDate: Yup.object<moment.Moment>().nullable(),
    arrivalDate: Yup.object<moment.Moment>().nullable(),
    account: Yup.object<ExpenseAccountSelectValue>()
      .nullable()
      .required(intl.formatMessage(messages.accountRequired)),
    attachment: Yup.string(),
    paymentMethod: Yup.object<RequestableCreditCardOption>()
      .nullable()
      .when(['reimbursable'], (reimbursable, fieldSchema) => {
        return reimbursable
          ? fieldSchema
          : fieldSchema.required(
              intl.formatMessage(messages.paymentTypeRequired)
            )
      }),
    currency: Yup.object<Currency>()
      .nullable()
      .required(intl.formatMessage(messages.currencyRequired)),
    totalCost: Yup.number().required(),
  })

  const initialValues = mapExpenseItemToFormSchema({
    item,
    baseCurrency,
    intl,
    reimbursementSetting,
    attachment,
    mileageConfiguration,
    mileageLockSetting,
    PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS,
  })

  const formik = useFormik<ExpenseLineItemFormSchema>({
    initialValues,
    validationSchema,
    onSubmit,
  })

  useEffect(() => {
    if (formRef) {
      formRef.current = { submitForm: formik.submitForm }
    }
  }, [formRef, formik.submitForm])

  useEffect(() => {
    if (attachment !== formik.values.attachment) {
      formik.setFieldValue('attachment', attachment)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formik.setFieldValue is not referentially stable in Formik 2.x
  }, [attachment])

  const unitOptions = [
    { label: intl.formatMessage(messages.unitKm), value: 'km' },
    { label: intl.formatMessage(messages.unitMi), value: 'mi' },
  ]

  const isNonReimbursable = !formik.values.reimbursable

  const isMeals = formik.values.expenseType === ExpenseTypes.MEALS
  const isMileage = formik.values.expenseType === ExpenseTypes.MILEAGE
  const isLabour = formik.values.expenseType === ExpenseTypes.LABOUR
  const isAirfare = formik.values.expenseType === ExpenseTypes.AIRFARE

  // Subtotal for Labour/Mileage is rate × quantity and is display-only.
  const { computedSubtotal, computedTotal } =
    computeExpenseLineItemTotalsUseCase.execute({
      expenseType: formik.values.expenseType,
      subtotal: formik.values.subtotal,
      tax: toTaxMode(formik.values.taxType, formik.values.taxAmount),
      tips: formik.values.tips,
      mileageRate: formik.values.mileageRate,
      distance: formik.values.distance,
      hourlyRate: formik.values.hourlyRate,
      hoursOfWork: formik.values.hoursOfWork,
    })

  // Forward: any input feeding into total changes → write totalCost.
  useEffect(() => {
    if (computedTotal !== formik.values.totalCost) {
      formik.setFieldValue('totalCost', computedTotal)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formik.setFieldValue is not referentially stable in Formik 2.x
  }, [computedTotal])

  // Reverse: user edits total → back-solve subtotal. The compute operations
  // are mutual inverses, so when the forward effect writes totalCost above,
  // this effect recomputes the same subtotal and the guard prevents a loop.
  useEffect(() => {
    const { computedSubtotal: reverseSubtotal } =
      computeExpenseLineItemTotalsUseCase.executeReverse({
        expenseType: formik.values.expenseType,
        total: formik.values.totalCost,
        tax: toTaxMode(formik.values.taxType, formik.values.taxAmount),
        tips: formik.values.tips,
      })
    if (reverseSubtotal !== formik.values.subtotal) {
      formik.setFieldValue('subtotal', reverseSubtotal)
    }
    // Narrow dep list intentional: the reverse effect's purpose is to react
    // only to user edits of totalCost. Other inputs (taxType/taxAmount/tips/
    // subtotal/expenseType/mileage-or-labour flags) already flow through
    // forward sync via `computedTotal`, which writes totalCost and retriggers
    // this effect indirectly. Listing them here creates a ping-pong loop with
    // the custom-tax sync effect because reverse back-solves subtotal, which
    // perturbs computedTotal by a rounding cent and re-enters the cycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.totalCost])

  // Sync displayed taxAmount when a custom tax is selected: tax = subtotal × percentage
  // Matches Angular's selectTax → calculate.amount chain.
  useEffect(() => {
    if (isCustomTax(formik.values.taxType)) {
      const pct = parsePercentageOrZero(formik.values.taxType.percentage)
      const computedTaxAmount = round2(formik.values.subtotal * pct)
      if (computedTaxAmount !== formik.values.taxAmount) {
        formik.setFieldValue('taxAmount', computedTaxAmount)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formik.setFieldValue is not referentially stable in Formik 2.x
  }, [formik.values.taxType, formik.values.subtotal])

  const isExpenseTypeInitialized = useRef(false)
  useEffect(() => {
    if (
      !PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS &&
      formik.values.expenseType === ExpenseTypes.MILEAGE &&
      mileageLockSetting?.currency
    ) {
      if (mileageLockSetting?.isLocked || isExpenseTypeInitialized.current) {
        formik.setFieldValue('currency', mileageLockSetting.currency)
      }
    }

    if (
      PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS &&
      formik.values.expenseType === ExpenseTypes.MILEAGE
    ) {
      if (
        isExpenseTypeInitialized.current &&
        mileageConfiguration?.defaultMileagePreset &&
        !formik.values.mileageRatePreset
      ) {
        const defaultMileagePreset = mileageConfiguration?.defaultMileagePreset
        formik.setFieldValue('mileageRatePreset', defaultMileagePreset)
        formik.setFieldValue('mileageRate', defaultMileagePreset.rate)
        formik.setFieldValue('unitOfDistanceStr', {
          value:
            defaultMileagePreset.unit === MileageRatePresetUnits.MILES
              ? UnitsOfDistance.MILES
              : UnitsOfDistance.KILOMETERS,
          label:
            defaultMileagePreset.unit === MileageRatePresetUnits.MILES
              ? intl.formatMessage(messages.unitMi)
              : intl.formatMessage(messages.unitKm),
        })
        formik.setFieldValue('currency', defaultMileagePreset.currency ?? null)
      }
      if (
        mileageConfiguration?.taxType ===
        ExpenseMileageConfigurationTaxTypes.NO_TAX
      ) {
        formik.setFieldValue('taxAmount', 0)
        formik.setFieldValue('taxType', CUSTOM_TAX_TYPE_OPTIONS[0])
      }
    }

    if (!isExpenseTypeInitialized.current) {
      isExpenseTypeInitialized.current = true
    }
  }, [formik.values.expenseType])

  const currencyName = formik.values.currency?.name ?? ('' as CurrencyName)

  const handleCurrencyChange = (currency: Currency | null) => {
    formik.setFieldValue('currency', currency)
  }

  return (
    <FormikProvider value={formik}>
      <Form.Content noValidate autoComplete='off'>
        <Space>
          <ExpenseTypeSelector
            value={formik.values.expenseType}
            onChange={(typeId) => formik.setFieldValue('expenseType', typeId)}
            disabled={isSubmitting}
          />

          <Divider />

          <Space direction='horizontal' justify='start'>
            <Form.Toggle
              id='reimbursable'
              name='reimbursable'
              label={intl.formatMessage(messages.needsReimbursementLabel)}
              checked={formik.values.reimbursable}
              disabled={isReimbursementLocked || isSubmitting}
            />

            <Space.Item grow={1}>
              {isNonReimbursable && (
                <WithFormControlSelect
                  id='paymentMethod'
                  name='paymentMethod'
                  label={intl.formatMessage(messages.paymentMethodLabel)}
                  required
                >
                  <RequestableCreditCardSelect
                    inputId='paymentMethod-select'
                    userId={requesterId}
                    value={formik.values.paymentMethod}
                    onChange={(option) =>
                      formik.setFieldValue('paymentMethod', option)
                    }
                    isDisabled={isSubmitting}
                    menuPortalTarget={document.body}
                  />
                </WithFormControlSelect>
              )}
            </Space.Item>
          </Space>

          <Space direction='horizontal'>
            <Space.Item grow={1}>
              <Form.TextField
                id='merchant'
                name='merchant'
                label={intl.formatMessage(messages.vendorNameLabel)}
                required
                placeholder={intl.formatMessage(messages.vendorNamePlaceholder)}
                disabled={isSubmitting}
                inputProps={{ maxLength: 100 }}
              />
            </Space.Item>
            <Form.SingleDatePicker
              id='transDate'
              name='transDate'
              label={intl.formatMessage(messages.dateLabel)}
              required
              disabled={isSubmitting}
              isOutsideRange={() => false}
              fullWidth
            />
          </Space>

          <WithFormControlSelect
            name='account'
            id='account'
            labelFor='accountCode-select'
            label={intl.formatMessage(messages.accountCodeLabel)}
            required
          >
            <ExpenseAccountCodeSelect
              inputId='accountCode-select'
              aria-labelledby='accountCode-label'
              secondaryCategoryId={secondaryCategoryId}
              requesterId={requesterId}
              isDisabled={isSubmitting}
              menuPortalTarget={document.body}
            />
          </WithFormControlSelect>

          <Form.TextField
            id='notes'
            name='notes'
            label={intl.formatMessage(messages.notesLabel)}
            placeholder={intl.formatMessage(messages.notesPlaceholder)}
            disabled={isSubmitting}
          />

          <Divider />

          {isMeals && (
            <MealsFields
              currencyName={currencyName}
              selectedCurrency={formik.values.currency}
              onCurrencyChange={handleCurrencyChange}
              isSubmitting={isSubmitting}
            />
          )}

          {isMileage && (
            <MileageFields
              currencyName={currencyName}
              selectedCurrency={formik.values.currency}
              onCurrencyChange={handleCurrencyChange}
              unitOptions={unitOptions}
              computedSubtotal={computedSubtotal}
              computedTotal={formik.values.totalCost}
              isSubmitting={isSubmitting}
              mileageConfiguration={mileageConfiguration}
              mileageLockSetting={mileageLockSetting}
            />
          )}

          {isLabour && (
            <LabourFields
              currencyName={currencyName}
              selectedCurrency={formik.values.currency}
              onCurrencyChange={handleCurrencyChange}
              computedSubtotal={computedSubtotal}
              computedTotal={formik.values.totalCost}
              isSubmitting={isSubmitting}
            />
          )}

          {isAirfare && (
            <AirfareFields
              currencyName={currencyName}
              selectedCurrency={formik.values.currency}
              onCurrencyChange={handleCurrencyChange}
              isSubmitting={isSubmitting}
            />
          )}

          {!isMeals && !isMileage && !isLabour && !isAirfare && (
            <DefaultFields
              currencyName={currencyName}
              selectedCurrency={formik.values.currency}
              onCurrencyChange={handleCurrencyChange}
              isSubmitting={isSubmitting}
            />
          )}
        </Space>
      </Form.Content>
    </FormikProvider>
  )
}
